import React, { useState, useEffect } from 'react';
import {
  Bell, BellOff, BellRing, Save, RotateCcw, Eye,
  Users, Shield, ChevronDown, ChevronUp, X, FileText
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Tabs, TabsContent, TabsList, TabsTrigger
} from '../../components/ui/tabs';
import {
  Dialog, DialogContent,
  DialogDescription, DialogHeader, DialogTitle
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { adminService } from '../../services/adminService';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
// Auth is carried by the httpOnly cookie; just set the JSON content type.
const getHeaders = () => ({ 'Content-Type': 'application/json' });

const NotificationSettings = () => {
  const [settings, setSettings] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editTemplate, setEditTemplate] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [saving, setSaving] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [settingsResp, templatesResp] = await Promise.all([
        fetch(`${API_URL}/api/notifications/admin/settings`, { credentials: 'include', headers: getHeaders() }).then(r => r.json()),
        fetch(`${API_URL}/api/notifications/admin/templates`, { credentials: 'include', headers: getHeaders() }).then(r => r.json()),
      ]);
      setSettings(settingsResp);
      setTemplates(templatesResp);
    } catch (error) {
      toast.error('Failed to load notification settings');
    } finally {
      setLoading(false);
    }
  };

  const toggleSetting = async (notificationType, currentEnabled) => {
    const newEnabled = !currentEnabled;
    setSaving(prev => ({ ...prev, [notificationType]: true }));
    try {
      await fetch(`${API_URL}/api/notifications/admin/settings`, {
        method: 'PUT',
        credentials: 'include',
        headers: getHeaders(),
        body: JSON.stringify({ notification_type: notificationType, enabled: newEnabled }),
      });
      setSettings(prev => prev.map(s =>
        s.notification_type === notificationType ? { ...s, enabled: newEnabled } : s
      ));
      toast.success(`${newEnabled ? 'Enabled' : 'Disabled'} notification`);
    } catch {
      toast.error('Failed to update setting');
    } finally {
      setSaving(prev => ({ ...prev, [notificationType]: false }));
    }
  };

  const handleSaveTemplate = async () => {
    if (!editTemplate) return;
    try {
      await fetch(`${API_URL}/api/notifications/admin/templates`, {
        method: 'PUT',
        credentials: 'include',
        headers: getHeaders(),
        body: JSON.stringify({
          notification_type: editTemplate.notification_type,
          subject: editTemplate.subject,
          html_body: editTemplate.html_body,
        }),
      });
      toast.success('Template saved');
      setEditTemplate(null);
      loadData();
    } catch {
      toast.error('Failed to save template');
    }
  };

  const handleResetTemplate = async (notificationType) => {
    try {
      await fetch(`${API_URL}/api/notifications/admin/templates/${notificationType}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getHeaders(),
      });
      toast.success('Template reset to default');
      loadData();
    } catch {
      toast.error('Failed to reset template');
    }
  };

  const handlePreview = (template) => {
    const html = template.html_body || `<p>Hi {{client_name}},</p><p>{{message}}</p>`;
    const rendered = html
      .replace(/\{\{title\}\}/g, template.label || 'Notification')
      .replace(/\{\{message\}\}/g, 'This is a preview of the notification message.')
      .replace(/\{\{client_name\}\}/g, 'John Smith')
      .replace(/\{\{link\}\}/g, '#');
    setPreviewHtml(rendered);
    setPreviewOpen(true);
  };

  const clientSettings = settings.filter(s => s.audience === 'client');
  const adminSettings = settings.filter(s => s.audience === 'admin');

  const clientTemplates = templates.filter(t => t.audience === 'client');
  const adminTemplates = templates.filter(t => t.audience === 'admin');

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin h-10 w-10 border-b-2 border-amber-500 rounded-full" />
      </div>
    );
  }

  const SettingRow = ({ setting }) => (
    <div className="flex items-center justify-between py-3 px-4 border-b last:border-b-0" data-testid={`setting-${setting.notification_type}`}>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900">{setting.label}</p>
        <p className="text-xs text-gray-500">{setting.description}</p>
      </div>
      <Switch
        checked={setting.enabled}
        onCheckedChange={() => toggleSetting(setting.notification_type, setting.enabled)}
        disabled={saving[setting.notification_type]}
        data-testid={`toggle-${setting.notification_type}`}
      />
    </div>
  );

  const TemplateRow = ({ template }) => (
    <div className="flex items-center justify-between py-3 px-4 border-b last:border-b-0" data-testid={`template-${template.notification_type}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-gray-900">{template.label}</p>
          {template.is_custom && <Badge variant="outline" className="text-xs">Custom</Badge>}
        </div>
        <p className="text-xs text-gray-500 mt-0.5">Subject: {template.subject}</p>
      </div>
      <div className="flex items-center gap-1.5">
        <Button variant="ghost" size="sm" onClick={() => handlePreview(template)} data-testid={`preview-${template.notification_type}`}>
          <Eye className="h-3.5 w-3.5 mr-1" /> Preview
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setEditTemplate({ ...template })} data-testid={`edit-template-${template.notification_type}`}>
          <FileText className="h-3.5 w-3.5 mr-1" /> Edit
        </Button>
        {template.is_custom && (
          <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleResetTemplate(template.notification_type)} data-testid={`reset-${template.notification_type}`}>
            <RotateCcw className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="notification-settings-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Notification Settings</h1>
        <p className="text-sm text-gray-500 mt-1">Control which notifications are sent and customize email templates</p>
      </div>

      <Tabs defaultValue="toggles">
        <TabsList>
          <TabsTrigger value="toggles" data-testid="tab-toggles">
            <BellRing className="h-4 w-4 mr-1.5" /> Enable / Disable
          </TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">
            <FileText className="h-4 w-4 mr-1.5" /> Email Templates
          </TabsTrigger>
        </TabsList>

        {/* ─── Toggles Tab ─── */}
        <TabsContent value="toggles" className="space-y-6 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="h-5 w-5 text-amber-500" /> Client Notifications
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {clientSettings.map(s => <SettingRow key={s.notification_type} setting={s} />)}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="h-5 w-5 text-blue-500" /> Admin Notifications
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {adminSettings.map(s => <SettingRow key={s.notification_type} setting={s} />)}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Templates Tab ─── */}
        <TabsContent value="templates" className="space-y-6 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="h-5 w-5 text-amber-500" /> Client Email Templates
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {clientTemplates.map(t => <TemplateRow key={t.notification_type} template={t} />)}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="h-5 w-5 text-blue-500" /> Admin Email Templates
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {adminTemplates.map(t => <TemplateRow key={t.notification_type} template={t} />)}
            </CardContent>
          </Card>

          <div className="p-4 bg-gray-50 rounded-lg border text-sm text-gray-600">
            <p className="font-medium mb-1">Template Variables</p>
            <p>Use these placeholders in your templates: <code className="bg-gray-200 px-1 rounded">{'{{client_name}}'}</code>, <code className="bg-gray-200 px-1 rounded">{'{{title}}'}</code>, <code className="bg-gray-200 px-1 rounded">{'{{message}}'}</code>, <code className="bg-gray-200 px-1 rounded">{'{{link}}'}</code></p>
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit Template Dialog */}
      <Dialog open={!!editTemplate} onOpenChange={(open) => { if (!open) setEditTemplate(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Email Template: {editTemplate?.label}</DialogTitle>
            <DialogDescription className="sr-only">Edit Email Template: {editTemplate?.label} dialog. Use the form below to continue.</DialogDescription>
          </DialogHeader>
          {editTemplate && (
            <div className="space-y-4 mt-2">
              <div>
                <Label>Email Subject</Label>
                <Input
                  value={editTemplate.subject}
                  onChange={(e) => setEditTemplate(prev => ({ ...prev, subject: e.target.value }))}
                  className="mt-1"
                  data-testid="template-subject-input"
                />
              </div>
              <div>
                <Label>HTML Body</Label>
                <Textarea
                  value={editTemplate.html_body}
                  onChange={(e) => setEditTemplate(prev => ({ ...prev, html_body: e.target.value }))}
                  className="mt-1 font-mono text-sm"
                  rows={12}
                  placeholder={'<p>Hi {{client_name}},</p>\n<p>{{message}}</p>'}
                  data-testid="template-body-input"
                />
                <p className="text-xs text-gray-400 mt-1">Use HTML. Available: {'{{client_name}}'}, {'{{title}}'}, {'{{message}}'}, {'{{link}}'}</p>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => handlePreview(editTemplate)}>
                  <Eye className="h-4 w-4 mr-1" /> Preview
                </Button>
                <Button className="bg-amber-500 hover:bg-amber-600" onClick={handleSaveTemplate} data-testid="save-template-btn">
                  <Save className="h-4 w-4 mr-1" /> Save Template
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Email Preview</DialogTitle>
            <DialogDescription className="sr-only">Email Preview dialog. Use the form below to continue.</DialogDescription>
          </DialogHeader>
          <div className="mt-2 border rounded-lg p-4 bg-gray-50">
            <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NotificationSettings;
